import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur, dateFmt, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, Th, Td } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { PackageMinus, FileText, FileSpreadsheet, Trash2, CheckCircle2, Circle, Upload } from "lucide-react";

export default function OwnWithdrawals({ embedded = false }) {
  const { user } = useAuth();
  const { t, tp } = useI18n();
  const isAdmin = user?.role === "admin";
  const canImport = can(user, "ownwithdrawals:import");
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState([]);
  const [year, setYear] = useState("alle");
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = year === "alle" ? "" : `?year=${year}`;
    api.get(`/own-withdrawals${q}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/own-withdrawals/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const openImport = () => {
    setImportOpen(true); setPreview(null); setMapping({});
    api.get("/inventory").then((r) => setProducts([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => setProducts([]));
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/own-withdrawals/import/preview", fd);
      setPreview(r.data.rows || []);
      const m = {};
      (r.data.rows || []).forEach((row) => { if (row.suggested_variant_id) m[row.excel_row] = row.suggested_variant_id; });
      setMapping(m);
      toast.success(t("common.rowsRead", { count: (r.data.rows || []).length }));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const confirmImport = async () => {
    const items = (preview || [])
      .filter((row) => mapping[row.excel_row] && mapping[row.excel_row] !== "__none__" && (row.qty_2026 || 0) > 0)
      .map((row) => ({ variant_id: mapping[row.excel_row], qty_2026: row.qty_2026 || 0 }));
    if (!items.length) { toast.error(t("common.mapAtLeastOne")); return; }
    if (!window.confirm(t("ow.importConfirm", { count: items.length, year: 2026 }))) return;
    setBusy(true);
    try {
      const r = await api.post("/own-withdrawals/import/confirm", { items });
      toast.success(`${t("ow.importDone", { created: r.data.created })}${r.data.skipped ? ` · ${r.data.skipped} ${t("common.skipped")}` : ""}`);
      setImportOpen(false); setPreview(null); setMapping({}); setYear("2026"); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const togglePaid = async (id) => {
    try { await api.post(`/own-withdrawals/${id}/toggle-paid`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("ow.cancelConfirm"))) return;
    try { await api.delete(`/own-withdrawals/${id}`); toast.success(t("ow.cancelled")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const filter = useListFilter(rows, { fields: ["product_name", "sku", "note", "created_by_email"], dateField: "occurred_at",
    statusOf: (r) => (r.ek_paid ? "paid" : "open"), storageKey: "ownWithdrawals" });
  const view = filter.filtered;
  const totals = view.reduce((a, r) => {
    a.total += r.total_cost || 0;
    if (r.ek_paid) a.paid += r.total_cost || 0; else a.open += r.total_cost || 0;
    return a;
  }, { total: 0, paid: 0, open: 0 });
  const years = summary.map((s) => s.year);

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t("ow.title")} subtitle={embedded ? "" : t("ow.subtitle")} compact={embedded}
        actions={canImport && <Button variant="outline" onClick={openImport} data-testid="ownw-import-btn"><Upload className="h-4 w-4 mr-1.5" /> {t("ow.excelImport")}</Button>} />

      <div className="mb-4 flex flex-wrap items-end gap-4 shrink-0">
        <div>
          <div className="text-xs text-muted-foreground mb-1">{t("common.year")}</div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40" data-testid="ownw-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">{t("common.allYears")}</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {year !== "alle" && (
          <>
            <Button variant="outline" onClick={() => nav(`/eigenentnahmen/report/${year}`)} data-testid="ownw-report-btn">
              <FileText className="h-4 w-4 mr-1.5" /> {t("ow.yearReport", { year })}
            </Button>
            <Button variant="outline" onClick={() => downloadFile(`/own-withdrawals/export.xlsx?year=${year}`, `eigenentnahmen_${year}.xlsx`)} data-testid="ownw-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel {year}
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 shrink-0">
        <div className="rounded-md border border-border bg-card p-4 shadow-sm" data-testid="ownw-total">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("ow.costTotal")}</div>
          <div className="font-mono text-2xl font-bold">{eur(totals.total)}</div>
        </div>
        <div className="rounded-md border border-border bg-card p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("ow.costPaid")}</div>
          <div className="font-mono text-2xl font-bold text-primary">{eur(totals.paid)}</div>
        </div>
        <div className="rounded-md border-2 border-destructive/60 bg-card p-4 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("ow.costOpen")}</div>
          <div className="font-mono text-2xl font-bold text-destructive">{eur(totals.open)}</div>
        </div>
      </div>

      <ListFilter filter={filter} testId="ownw" withDates withStatus placeholder={t("filter.searchProductSku")}
        statusLabel={(s) => (s === "paid" ? t("filter.statusPaid") : t("filter.statusOpen"))}
        exportName="eigenentnahmen" exportColumns={[
          { label: t("common.date"), value: (r) => dateFmt(r.occurred_at) },
          { label: t("common.product"), value: (r) => r.product_name },
          { label: "SKU", value: (r) => r.sku },
          { label: t("common.quantity"), value: (r) => r.quantity },
          { label: t("ow.costPerUnit"), value: (r) => csvMoney(r.unit_cost) },
          { label: t("ow.costSum"), value: (r) => csvMoney(r.total_cost) },
          { label: t("common.person"), value: (r) => r.created_by_email || "" },
          { label: t("common.note"), value: (r) => r.note || "" },
          { label: t("ow.costPaidShort"), value: (r) => (r.ek_paid ? t("filter.statusPaid") : t("filter.statusOpen")) },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        view.length === 0 ? <EmptyState testId="ownw-empty" icon={PackageMinus} title={t("ow.empty")} hint={t("ow.emptyHint")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("common.date")}</Th><Th sticky>{t("common.product")} / SKU</Th><Th sticky right>{t("common.quantity")}</Th>
                <Th sticky right>{t("ow.costPerUnit")}</Th><Th sticky right>{t("ow.costSum")}</Th>
                <Th sticky>{t("common.person")}</Th><Th sticky>{t("common.note")}</Th><Th sticky>{t("ow.costPaidShort")}</Th><Th sticky></Th>
              </tr></thead>
              <tbody data-testid="ownw-table">
                {view.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/40 transition-colors">
                    <Td className="whitespace-nowrap">{dateFmt(r.occurred_at)}</Td>
                    <Td><div className="font-medium">{r.product_name}</div><div className="text-xs text-muted-foreground font-mono">{r.sku}</div></Td>
                    <Td right mono>{r.quantity}</Td>
                    <Td right mono>{eur(r.unit_cost)}</Td>
                    <Td right mono className="font-semibold">{eur(r.total_cost)}</Td>
                    <Td className="text-xs">{r.created_by_email || "—"}</Td>
                    <Td className="text-xs text-muted-foreground max-w-[220px] truncate" title={r.note}>{r.note || "—"}</Td>
                    <Td>
                      <button onClick={() => togglePaid(r.id)} data-testid={`ownw-paid-${r.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${r.ek_paid ? "bg-primary/15 text-primary" : "bg-destructive/10 text-destructive"}`}>
                        {r.ek_paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                        {r.ek_paid ? t("common.paid") : t("common.open")}
                      </button>
                    </Td>
                    <Td right>{isAdmin && <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`ownw-del-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={importOpen} onOpenChange={(o) => !o && setImportOpen(false)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{t("ow.importTitle", { year: 2026 })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            {t("ow.importHint")}
          </p>
          {!preview ? (
            <div className="py-6">
              <Input type="file" accept=".xlsx" onChange={onFile} disabled={busy} data-testid="ownw-import-file" />
              {busy && <p className="text-sm text-muted-foreground mt-2">{t("common.readingFile")}</p>}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0"><tr>
                  <Th>{t("common.excelRow")}</Th><Th right>{t("ow.info2025", { year: 2025 })} (AO)</Th><Th right>2026 (AP)</Th><Th>{t("common.mapProduct")}</Th>
                </tr></thead>
                <tbody data-testid="ownw-import-preview">
                  {preview.map((row) => (
                    <tr key={row.excel_row} className={mapping[row.excel_row] ? "" : "bg-destructive/5"}>
                      <Td><div className="max-w-[280px] truncate" title={row.excel_name}>{row.excel_name}</div>{row.ean && <div className="text-xs text-muted-foreground font-mono">EAN {row.ean}</div>}</Td>
                      <Td right mono className="text-muted-foreground">{row.qty_2025}</Td>
                      <Td right mono>{row.qty_2026}</Td>
                      <Td>
                        <SearchSelect
                          testId={`ownw-map-${row.excel_row}`}
                          className="w-72"
                          value={mapping[row.excel_row] || "__none__"}
                          onChange={(v) => setMapping({ ...mapping, [row.excel_row]: v })}
                          placeholder={t("common.dontImport")}
                          options={[{ value: "__none__", label: t("common.dontImport") }, ...products.map((p) => ({ value: p.variant_id, label: `${p.product_name} · ${p.sku}` }))]}
                        />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            {preview && <Button variant="outline" onClick={() => { setPreview(null); setMapping({}); }} data-testid="ownw-import-reset">{t("common.otherFile")}</Button>}
            {preview && <Button onClick={confirmImport} disabled={busy} data-testid="ownw-import-confirm">{t("common.confirmImport")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
